import CardState, { type CardStateValue } from '@/components/ui/CardState'
import EmptyState from '@/components/ui/EmptyState'
import SectionHead from '@/components/ui/SectionHead'
import SeeAllLink from '@/components/ui/SeeAllLink'
import Skeleton from '@/components/ui/Skeleton'
import SymbolLink from '@/components/ui/SymbolLink'
import { useShareUnlocks } from '@/features/market-rail/api/queries'
import { SHARE_UNLOCKS_AVAILABLE } from '@/features/market-rail/api/shareUnlocksService'
import RailRows from '@/features/market-rail/components/RailRows'
import { useRailBlock } from '@/features/market-rail/useRailFit'
import { useManualRefresh } from '@/hooks/useManualRefresh'
import { toBsDate } from '@/lib/bsDate'
import { daysFromToday } from '@/lib/relativeAge'
import { cn } from '@/lib/utils'
import type { ShareHolder, ShareUnlock } from '@/schemas/share-unlocks'

/** The mock draws four. */
const RAIL_ROWS = 4

/** A release within this many days is the one worth a gold chip. */
const SOON_DAYS = 7

const HOLDER_LABEL: Record<ShareHolder, string> = {
  promoter: 'Promoter',
  mutual_fund: 'Mutual fund',
}

/** Whole days from today (Kathmandu's, not the device's) to the unlock; the schema has already
 *  checked the date's shape, so it always reads. */
const daysUntil = (isoDate: string): number => daysFromToday(isoDate) ?? 0

/** "28.5 L", "1.4 Cr", "96 L": the rupee ladder's units on a count of shares, trailing zeros off. */
const kittaLabel = (quantity: number): string => {
  const [unit, suffix] =
    quantity >= 1e7
      ? [1e7, ' Cr']
      : quantity >= 1e5
        ? [1e5, ' L']
        : quantity >= 1e3
          ? [1e3, ' K']
          : [1, '']
  return `${String(Number((quantity / unit).toFixed(2)))}${suffix}`
}

const inDays = (days: number): string =>
  days <= 0 ? 'today' : days === 1 ? 'in 1 day' : `in ${String(days)} days`

/**
 * One unlock row (`rail-lock__row`): a date tile (the BS day over its month's first three
 * letters, a gold bar on top), the symbol over "Promoter · 28.5 L kitta", and a chip with the days
 * left, gold-washed when it is a week or less away. The row is the symbol's link and takes the
 * rail's hover wash (`rail-row-wash`); the symbol goes to the foreground on hover.
 */
function UnlockRow({ item }: { item: ShareUnlock }) {
  const bs = toBsDate(item.unlockDate)
  const days = daysUntil(item.unlockDate)
  const soon = days <= SOON_DAYS

  return (
    <div className="group relative flex items-center gap-3 border-b border-border-subtle py-2 last:border-b-0 rail-row-wash">
      <span
        aria-hidden="true"
        className="flex h-10 w-9 shrink-0 flex-col items-center justify-center border-t-4 border-accent bg-card"
      >
        <span className="tnum text-h-s leading-4 text-foreground">
          {bs === null ? '—' : bs.format('D', 'en')}
        </span>
        <span className="text-rail-tile tracking-wide text-muted-foreground-subtle uppercase">
          {bs === null ? '' : bs.format('MMMM', 'en').slice(0, 3)}
        </span>
      </span>
      <span className="flex min-w-0 flex-1 flex-col">
        <SymbolLink
          symbol={item.symbol}
          title={`${item.name}: ${kittaLabel(item.quantity)} kitta of ${HOLDER_LABEL[item.holder].toLowerCase()} shares can be sold from ${bs?.format('D MMMM', 'en') ?? item.unlockDate}`}
          className="truncate text-h-s text-muted-foreground after:absolute after:inset-0 after:content-[''] group-hover:text-foreground hover:no-underline"
        />
        <span className="truncate text-body-xs text-muted-foreground-subtle">
          <span className="font-semibold text-muted-foreground">{HOLDER_LABEL[item.holder]}</span>
          {' · '}
          {kittaLabel(item.quantity)} kitta
        </span>
      </span>
      <span
        className={cn(
          'inline-flex h-5 shrink-0 items-center px-2 text-body-xs',
          soon ? 'bg-accent/18 text-foreground' : 'bg-card text-muted-foreground',
        )}
      >
        {inDays(days)}
      </span>
    </div>
  )
}

function UnlockRowSkeleton() {
  return (
    <div className="flex items-center gap-3 py-2">
      <Skeleton className="h-10 w-9 rounded-xs" />
      <div className="flex flex-1 flex-col gap-1">
        <Skeleton className="h-4 w-16 rounded-xs" />
        <Skeleton className="h-4 w-28 rounded-xs" />
      </div>
      <Skeleton className="h-5 w-14 rounded-xs" />
    </div>
  )
}

const SKELETON_ROWS = Array.from({ length: RAIL_ROWS }, (_, index) => (
  <UnlockRowSkeleton key={index} />
))

/**
 * The rail's "Shares unlocking soon" block (Home mock parity, 2026-10-28): the next promoter and
 * mutual-fund lock-ins to end, soonest first, so a member sees supply that is about to be free to
 * sell. "All" opens Market events, where the corporate calendar already lives.
 *
 * ── No data yet, by owner ruling ──────────────────────────────────────────
 *
 * mm-be has no lock-in data, so `SHARE_UNLOCKS_AVAILABLE` is `false`: the real head over the
 * generic "Not available yet" state, no request, no sample rows. Contract in `asks/rail.md`.
 *
 * The date tile is the BS day (the calendar a member reads a notice by); the chip's days are
 * counted on Kathmandu's clock. Both derive from the one AD `unlockDate` on the wire.
 */
export default function SharesUnlockingCard() {
  const block = useRailBlock('unlocks')
  const unlocks = useShareUnlocks()
  const { data, isPending, isError } = unlocks
  const manual = useManualRefresh([unlocks])

  const state: CardStateValue = isPending
    ? 'loading'
    : isError
      ? 'failed'
      : data.length === 0
        ? 'empty'
        : manual.isManual
          ? 'refreshing'
          : 'loaded'

  return (
    <section aria-label="Shares unlocking soon" {...block}>
      <SectionHead
        size="sm"
        title="Shares unlocking soon"
        action={
          <SeeAllLink to="/market/events" size="rail" aria-label="All upcoming events">
            All
          </SeeAllLink>
        }
      />
      <div className="mt-1">
        {SHARE_UNLOCKS_AVAILABLE ? (
          <CardState
            state={state}
            skeleton={<RailRows block="unlocks" rows={SKELETON_ROWS} />}
            onRetry={manual.refresh}
            failedTitle="Couldn't load unlocks"
            failedDescription="The unlock calendar is not answering. Try again."
            emptyTitle="Nothing unlocking soon"
            emptyDescription="Upcoming lock-in ends show here."
          >
            {data === undefined ? null : (
              <RailRows
                block="unlocks"
                rows={data
                  .filter((item) => daysUntil(item.unlockDate) >= 0)
                  .slice(0, RAIL_ROWS)
                  .map((item) => (
                    <UnlockRow
                      key={`${item.symbol}-${item.unlockDate}-${item.holder}`}
                      item={item}
                    />
                  ))}
              />
            )}
          </CardState>
        ) : (
          <EmptyState kind="not-available" thing="Shares unlocking soon" size="inline" />
        )}
      </div>
    </section>
  )
}
